/* site.mjs — the site map of record.
   ----------------------------------------------------------------------
   One file that says which public URLs exist, which languages they come
   in, which old URLs redirect where, and which URL prefixes are private.
   It is plain data with no React imports, and it is an .mjs file on
   purpose: the build scripts in scripts/ import it directly with Node,
   and the app imports it through webpack. Everything below is derived
   from it:

     src/App.js                    → the React Router route table
     scripts/generate-sitemap.mjs  → public/sitemap.xml
     scripts/prerender.mjs         → one static HTML file per URL
     src/components/SEO.jsx        → canonical, hreflang, robots

   ADDING A PUBLIC PAGE: add one entry to PUBLIC_ROUTES and map its
   component in App.js. The sitemap, the pre-rendered HTML and the
   hosting config follow automatically on the next build.

   ADDING A PRIVATE AREA (student, teacher or admin dashboards): put its
   URL prefix in PRIVATE_PREFIXES. Anything under it gets a noindex
   robots tag from SEO.jsx, is left out of the sitemap, is never
   pre-rendered, and is listed as Disallow in public/robots.txt (keep
   that file in step by hand; it is a plain text file crawlers read). */

export const SITE_URL = "https://kidsology.ae";

/* Switcher order: AR EN RU. English is the default and carries no
   prefix, so every existing inbound link keeps working. */
export const LOCALES = ["ar", "en", "ru"];
export const DEFAULT_LOCALE = "en";
const PREFIXED = LOCALES.filter((l) => l !== DEFAULT_LOCALE);

/* Public, indexable pages. `path` is the canonical English path.
   `sources` are the files whose last git commit date becomes the
   sitemap <lastmod>, so the date only moves when the page really
   changed. `priority` and `changefreq` are sitemap hints only. */
const LIVE_ROUTES = [
  { path: "/",                                    priority: 1.0, changefreq: "weekly",  sources: ["src/pages/Home.jsx", "src/data/offer.js", "src/data/programs.js", "src/data/trips.js"] },
  { path: "/programs",                            priority: 0.9, changefreq: "monthly", sources: ["src/pages/Programs.jsx", "src/data/offer.js"] },
  { path: "/programs/japanese-mental-arithmetic", priority: 0.7, changefreq: "monthly", sources: ["src/pages/programs/JapaneseMentalArithmetic.jsx"] },
  { path: "/programs/business-ai",                priority: 0.7, changefreq: "monthly", sources: ["src/pages/programs/ProgramSpecPage.jsx", "src/data/businessAI.js"] },
  { path: "/programs/finance-accounting",         priority: 0.7, changefreq: "monthly", sources: ["src/pages/programs/ProgramSpecPage.jsx", "src/data/finance.js"] },
  { path: "/programs/coding-with-ai",             priority: 0.7, changefreq: "monthly", sources: ["src/pages/programs/ProgramPage.jsx", "src/data/programPages.js"] },
  { path: "/programs/arts-craft",                 priority: 0.7, changefreq: "monthly", sources: ["src/pages/programs/ProgramPage.jsx", "src/data/programPages.js"] },
  { path: "/trips",                               priority: 0.9, changefreq: "weekly",  sources: ["src/pages/Trips.jsx", "src/pages/trips/TripBlocks.jsx", "src/data/camps.js"] },
  { path: "/trips/dubai-winter",                  priority: 1.0, changefreq: "weekly",  sources: ["src/pages/DubaiWinter.jsx", "src/data/trips.js"] },
  { path: "/give-and-learn",                      priority: 0.9, changefreq: "weekly",  sources: ["src/pages/GiveLearn.jsx"] },
  { path: "/autumn-term",                         priority: 0.9, changefreq: "weekly",  sources: ["src/pages/AutumnTerm.jsx"] },
  { path: "/trips/uk-2026",                       priority: 0.6, changefreq: "monthly", sources: ["src/pages/UK2026.jsx", "src/data/pastTrips.js"] },
  { path: "/trips/uk-summer",                     priority: 0.8, changefreq: "monthly", sources: ["src/pages/trips/CampPage.jsx", "src/data/camps.js"] },
  { path: "/trips/istanbul-bursa",                priority: 0.8, changefreq: "monthly", sources: ["src/pages/trips/CampPage.jsx", "src/data/camps.js"] },
  { path: "/trips/tokyo-osaka",                   priority: 0.8, changefreq: "monthly", sources: ["src/pages/trips/CampPage.jsx", "src/data/camps.js"] },
  { path: "/trips/beijing-shanghai",              priority: 0.8, changefreq: "monthly", sources: ["src/pages/trips/CampPage.jsx", "src/data/camps.js"] },
  { path: "/trips/moscow",                        priority: 0.8, changefreq: "monthly", sources: ["src/pages/trips/CampPage.jsx", "src/data/camps.js"] },
  { path: "/trips/madrid-barcelona",              priority: 0.8, changefreq: "monthly", sources: ["src/pages/trips/CampPage.jsx", "src/data/camps.js"] },
  { path: "/trips/rome-florence",                 priority: 0.8, changefreq: "monthly", sources: ["src/pages/trips/CampPage.jsx", "src/data/camps.js"] },
  { path: "/trips/zurich-geneva",                 priority: 0.8, changefreq: "monthly", sources: ["src/pages/trips/CampPage.jsx", "src/data/camps.js"] },
  { path: "/schools",                             priority: 0.9, changefreq: "monthly", sources: ["src/pages/Schools.jsx", "src/data/schools.js"] },
  { path: "/career-pathways",                     priority: 0.8, changefreq: "monthly", sources: ["src/pages/CareerPathways.jsx", "src/data/offer.js"] },
  { path: "/workshops",                           priority: 0.8, changefreq: "monthly", sources: ["src/pages/Workshops.jsx", "src/data/offer.js"] },
  { path: "/refund-policy",                       priority: 0.4, changefreq: "yearly",  sources: ["src/pages/RefundPolicy.jsx", "src/data/refundPolicy.js"] },
  { path: "/privacy-policy",                      priority: 0.4, changefreq: "yearly",  sources: ["src/pages/PrivacyPolicy.jsx", "src/data/privacyPolicy.js"] },
];

/* Pages that are built but NOT published yet. They are left out of the
   router, the sitemap and the pre-render, so their URLs 404 on the
   live site. Preview one locally with
     REACT_APP_INCLUDE_DRAFTS=1 npm run build
   (the flag is read by the app and by the build scripts). In src/App.js
   a draft page is loaded with a require behind the same flag, so a
   normal build leaves its text out of the bundle. To publish, move the
   entry into LIVE_ROUTES above and import the page normally in App.js.
   /refund-policy went live from here on 2026-10-06, /privacy-policy on
   2026-10-27. */
export const DRAFT_ROUTES = [
];

/* Written as one expression on purpose: the app build inlines
   process.env.REACT_APP_* at compile time (no `process` exists in the
   browser), and the Node build scripts read the real environment. */
const INCLUDE_DRAFTS = process.env.REACT_APP_INCLUDE_DRAFTS === "1";

export const PUBLIC_ROUTES = INCLUDE_DRAFTS ? [...LIVE_ROUTES, ...DRAFT_ROUTES] : LIVE_ROUTES;

/* True while a path is a draft (not in LIVE_ROUTES). Pages use it to
   hide links to unpublished pages. */
export function isDraftPath(path) {
  return DRAFT_ROUTES.some((r) => r.path === path) && !LIVE_ROUTES.some((r) => r.path === path);
}

/* Old URLs that still receive traffic. Firebase Hosting answers these
   with a real 301 (the "redirects" block in firebase.json lists each
   one in all three languages; keep it in step with this list). App.js
   keeps a client-side Navigate as a fallback for in-app navigation. */
export const LEGACY_REDIRECTS = [
  { from: "/trips/uk-summer-camp",           to: "/trips/uk-2026" },
  { from: "/trips/dubai-winter-camp",        to: "/trips/dubai-winter" },
  { from: "/programs/future-skills-mindset", to: "/programs" },
  { from: "/programs/performance-mindset",   to: "/programs" },
  /* Removed from the site "for now" by the owner, 2026-10-06. */
  { from: "/programs/elite-development",     to: "/programs" },
  { from: "/programs/sports-performance",    to: "/programs" },
  { from: "/programs/football-pathway",      to: "/programs" },
];

/* URL prefixes that must never be indexed: the API, and the future
   platform areas. Reserved now so the SEO layer already treats them as
   private the day they ship. */
export const PRIVATE_PREFIXES = [
  "/api",
  "/app",
  "/admin",
  "/dashboard",
  "/account",
  "/login",
  "/student",
  "/teacher",
  "/parent",
];

/* ── URL helpers (pure, shared by the app and the build scripts) ── */

/* Which language is this pathname in. */
export function localeFromPath(pathname = "/") {
  const first = String(pathname).split("/")[1]?.toLowerCase();
  return PREFIXED.includes(first) ? first : DEFAULT_LOCALE;
}

/* Drop the language prefix: "/ar/trips" → "/trips", "/ar" → "/". */
export function stripLocale(pathname = "/") {
  const segments = String(pathname).split("/");
  if (PREFIXED.includes(segments[1]?.toLowerCase())) {
    segments.splice(1, 1);
    const rest = segments.join("/");
    return rest === "" ? "/" : rest;
  }
  return pathname || "/";
}

/* Add the language prefix to an in-site path. Hash-only links,
   absolute URLs, mailto:, tel: and wa.me links are left untouched. */
export function localizePath(path = "/", locale = DEFAULT_LOCALE) {
  if (typeof path !== "string" || path === "") return path;
  if (!path.startsWith("/")) return path;
  const bare = stripLocale(path);
  if (locale === DEFAULT_LOCALE) return bare;
  if (bare === "/") return `/${locale}`;
  /* "/#contact" → "/ar#contact", never "/ar/#contact": the hosting
     layer 301s the trailing slash away, which would cost a reload. */
  if (bare.startsWith("/#") || bare.startsWith("/?")) return `/${locale}${bare.slice(1)}`;
  return `/${locale}${bare}`;
}

/* True when a path lives under a private prefix, in any language. */
export function isPrivatePath(pathname = "/") {
  const bare = stripLocale(pathname);
  return PRIVATE_PREFIXES.some((p) => bare === p || bare.startsWith(`${p}/`));
}

/* Every public URL path, all languages: "/", "/ar", "/ru", "/programs",
   "/ar/programs", … Used by the sitemap and the pre-renderer. */
export function allPublicPaths() {
  return PUBLIC_ROUTES.flatMap((route) =>
    LOCALES.map((locale) => localizePath(route.path, locale))
  );
}
